import { useSession } from '@/lib/auth-client';
import type { FeatureFlagKey, UserFeatureFlags } from '@sizzlenz/shared';

/**
 * Hook to check user feature flags
 *
 * @example
 * const { hasFeature, getFeature, flags } = useFeatures();
 *
 * if (hasFeature('approvedRetailer')) {
 *   // Show retailer features
 * }
 *
 * const customValue = getFeature('customFlag');
 */
export function useFeatures() {
  const { data: session } = useSession();

  const flags = (session?.user?.data?.flags as UserFeatureFlags) || {};

  /**
   * Check if a feature flag is enabled (truthy)
   */
  const hasFeature = (key: FeatureFlagKey): boolean => {
    return Boolean(flags[key]);
  };

  /**
   * Get the raw value of a feature flag
   */
  const getFeature = <T = boolean | string | number>(key: string): T | undefined => {
    return flags[key] as T | undefined;
  };

  return {
    flags,
    hasFeature,
    getFeature,
  };
}
